import { ref } from "vue";

const lineCount = 7;
const linePadding = 20;
const lineColor = "#cfa706ff";
const lineWidth = 2;

const scribbleColor = "#000";
const scribbleWidth = 2;

// --- State Variables ---
const canvas = ref(null);
const ctx = ref(null);
const isDrawing = ref(false);
const lastPoint = ref(null);
// Tracks the ID of the pointer currently drawing (e.g., the stylus)
const activePointerId = ref(null);

const allowedTypes = ["pen"];  // "pen"

/**
 * The core logic to finalize a stroke and reset the state.
 * It checks if the event belongs to the active pointer ID before cleaning up.
 * @param {PointerEvent | null} evt The pointer event object (can be null if called internally).
 */
const finalizeStroke = (evt) => {
  // Only finalize if we are currently drawing
  if (canvas.value == null || !isDrawing.value) {
    return;
  }

  // ⭐️ CRUCIAL FIX: Ensure only the pointer that STARTED the stroke can END it.
  // This blocks accidental touch/mouse events from ending the stylus stroke.
  if (evt && evt.pointerId && evt.pointerId !== activePointerId.value) {
    return;
  }

  // Release pointer capture if it was captured.
  if (evt && evt.pointerId && canvas.value.hasPointerCapture(evt.pointerId)) {
    canvas.value.releasePointerCapture(evt.pointerId);
  }

  // Complete the final segment to the last recorded point
  if (lastPoint.value) {
    ctx.value.lineTo(lastPoint.value.x, lastPoint.value.y);
    ctx.value.stroke();
  }

  // Reset the drawing state immediately
  isDrawing.value = false;
  lastPoint.value = null;
  activePointerId.value = null; // Clear the active ID to allow the next stroke to start
};

// --- Event Handlers (FIXED) ---

/**
 * Handles the pointerup event to finalize the stroke.
 * This function no longer filters by pointerType, relying entirely on the
 * activePointerId check inside finalizeStroke.
 * @param {PointerEvent} evt
 */
const handlePointerUp = (evt) => {
  console.info("handle pointer up");

  finalizeStroke(evt);
};

/**
 * Handles the pointerleave event to finalize the stroke.
 * This function no longer filters by pointerType, relying entirely on the
 * activePointerId check inside finalizeStroke.
 * @param {PointerEvent} evt
 */
const handlePointerLeave = (evt) => {
  console.info("handle pointer leave");

  finalizeStroke(evt);
};

/**
 * Attaches the necessary pointer event listeners to the canvas element.
 */
const addListeners = () => {
  if (canvas.value) {
    canvas.value.addEventListener("pointerdown", handleStart);
    canvas.value.addEventListener("pointermove", handleMove);

    // Use separate, explicit handlers for better control over stroke end conditions
    canvas.value.addEventListener("pointerup", handlePointerUp);
    canvas.value.addEventListener("pointerleave", handlePointerLeave);
  }
};

/**
 * Sets the canvas element, configures its size, gets the 2D context, and attaches listeners.
 * @param {HTMLCanvasElement} elem The canvas element reference.
 */
const setCanvasElement = (elem) => {
  canvas.value = elem;

  const displayWidth = canvas.value.clientWidth;
  const displayHeight = canvas.value.clientHeight;

  canvas.value.width = displayWidth;
  canvas.value.height = displayHeight;

  ctx.value = canvas.value.getContext("2d");

  ctx.value.lineWidth = scribbleWidth;
  ctx.value.strokeStyle = scribbleColor;
  ctx.value.lineJoin = "round";
  ctx.value.lineCap = "round";

  addListeners();
};

const addLines = () => {
  let width = canvas.value.width;
  let height = canvas.value.height;

  // let lineLength = width - linePadding * 2;
  let lineSpacing = height / (lineCount + 2);

  for (let i = 0; i < lineCount; i++) {
    ctx.value.strokeStyle = lineColor;
    ctx.value.lineWidth = lineWidth;
    ctx.value.beginPath();
    ctx.value.moveTo(linePadding, lineSpacing * (i + 1));
    ctx.value.lineTo(width - linePadding, lineSpacing * (i + 1));
    ctx.value.stroke();
  }
};

/**
 * Calculates the coordinates relative to the canvas drawing surface,
 * accounting for CSS scaling.
 * @param {PointerEvent} e The pointer event object.
 * @returns {[number, number] | undefined} Mapped [x, y] coordinates.
 */
const getCanvasCoords = (e) => {
  if (canvas.value == null || canvas.value.getBoundingClientRect == null) {
    return;
  }

  const rect = canvas.value.getBoundingClientRect();
  const cssX = e.clientX - rect.left;
  const cssY = e.clientY - rect.top;
  const scaleX = canvas.value.width / rect.width;
  const scaleY = canvas.value.height / rect.height;
  const mappedX = cssX * scaleX;
  const mappedY = cssY * scaleY;

  return [mappedX, mappedY];
};

/**
 * Handles the pointerdown event to begin a drawing stroke.
 * **Locks the stroke to the initiating pointer ID.**
 * @param {PointerEvent} evt
 */
const handleStart = (evt) => {
  if (canvas.value == null) {
    return;
  }

  // ⭐️ CRITICAL CHECK: Only allow 'pen' events to START a stroke.
  if (allowedTypes.length > 0 && !allowedTypes.includes(evt.pointerType)) {
    return;
  }

  console.info("handle start");

  // Stylus Lock: If a drawing session is already active, ignore this new start.
  if (activePointerId.value !== null) {
    return;
  }

  // Stylus Lock: Lock the session to this pointer's ID.
  activePointerId.value = evt.pointerId;

  // Capture the pointer to guarantee receiving pointerup/pointerleave events.
  canvas.value.setPointerCapture(evt.pointerId);

  evt.preventDefault();

  isDrawing.value = true;

  let coords = getCanvasCoords(evt);
  if (!coords) return;

  lastPoint.value = { x: coords[0], y: coords[1] };

  ctx.value.lineWidth = scribbleWidth;
  ctx.value.strokeStyle = scribbleColor;
  ctx.value.lineJoin = "round";
  ctx.value.lineCap = "round";

  ctx.value.beginPath();
  ctx.value.moveTo(lastPoint.value.x, lastPoint.value.y);
};

/**
 * Handles the pointermove event to continue the drawing stroke with smoothing.
 * **Ignores movement events from non-active pointers (e.g., palm touches).**
 * @param {PointerEvent} evt
 */
const handleMove = (evt) => {
  if (canvas.value == null || !isDrawing.value || lastPoint.value == null) {
    return;
  }

  // ⭐️ Stylus Lock: Only proceed if this event belongs to the active drawing pointer.
  // This correctly filters out all other pointers (touch, mouse) mid-stroke.
  if (evt.pointerId !== activePointerId.value) {
    return;
  }

  evt.preventDefault();

  let coords = getCanvasCoords(evt);
  if (!coords) return;

  let x = coords[0];
  let y = coords[1];

  // Smoothing: Calculate the midpoint for the quadratic curve.
  const midX = (lastPoint.value.x + x) / 2;
  const midY = (lastPoint.value.y + y) / 2;

  // Use quadraticCurveTo for smooth drawing
  ctx.value.quadraticCurveTo(lastPoint.value.x, lastPoint.value.y, midX, midY);
  ctx.value.stroke();

  // Set up the path for the next segment
  ctx.value.beginPath();
  ctx.value.moveTo(midX, midY);

  lastPoint.value = { x, y };
};

/**
 * Clears the entire canvas drawing area.
 */
const clear = () => {
  if (canvas.value == null || ctx.value == null) {
    return;
  }
  ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height);
  addLines();
};

/**
 * Function to clean up listeners (for component unmounting).
 */
const removeListeners = () => {
  if (canvas.value) {
    canvas.value.removeEventListener("pointerdown", handleStart);
    canvas.value.removeEventListener("pointermove", handleMove);
    canvas.value.removeEventListener("pointerup", handlePointerUp);
    // canvas.value.removeEventListener("pointerleave", handlePointerLeave);
  }
};

const asBASE64 = () => {
  if (canvas.value) {
    // Create a new temporary canvas for image processing
    const newCanvas = document.createElement("canvas");
    newCanvas.width = canvas.value.width;
    newCanvas.height = canvas.value.height;

    const ctx = newCanvas.getContext("2d");

    // Fill with white background (assuming no background was drawn on the original)
    ctx.fillStyle = "#FFF";
    ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);

    // Draw the existing canvas content onto the new one
    ctx.drawImage(canvas.value, 0, 0);

    return newCanvas.toDataURL();
  }
  return "";
};

// --- Exported Composition Function ---
export function useCanvas() {
  return {
    setCanvasElement,
    addLines,
    handleStart,
    handleMove,
    handlePointerUp,
    handlePointerLeave,
    clear,
    removeListeners,
    asBASE64,
  };
}
